// frontend/app/(nurse)/my-compliance/page.tsx
"use client";

import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  FileCheck,
  RefreshCw,
  XCircle,
  Shield,
  Upload,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useNurseCredentials,
  computeCredentialStats,
  NurseCredential,
  CredentialStatus,
  ExpiryStatus,
} from "@/lib/hooks/useNurseCredentials";
import {
  useNurseApplications,
  ApplicationWithCompliance,
  ComplianceStatus,
} from "@/lib/hooks/useNurseApplications";
import { NotificationsBell } from "@/components/notifications";
import { CredentialCard } from "@/components/features/CredentialCard";
import { CredentialUpload } from "@/components/features/CredentialUpload";
import { CredentialDetailModal } from "@/components/features/CredentialDetailModal";

// =============================================================================
// Summary Card Component
// =============================================================================

interface SummaryCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  severity?: "success" | "warning" | "danger" | "info";
}

function SummaryCard({ label, value, icon, severity }: SummaryCardProps) {
  return (
    <div
      className={cn(
        "rounded-lg border p-4",
        severity === "danger" && "border-red-200 bg-red-50",
        severity === "warning" && "border-amber-200 bg-amber-50",
        severity === "success" && "border-emerald-200 bg-emerald-50",
        severity === "info" && "border-blue-200 bg-blue-50",
        !severity && "border-slate-200 bg-white"
      )}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {label}
          </div>
          <div className="mt-2 text-3xl font-bold text-slate-900">{value}</div>
        </div>
        <div
          className={cn(
            "rounded-full p-2",
            severity === "danger" && "bg-red-100 text-red-600",
            severity === "warning" && "bg-amber-100 text-amber-600",
            severity === "success" && "bg-emerald-100 text-emerald-600",
            severity === "info" && "bg-blue-100 text-blue-600",
            !severity && "bg-slate-100 text-slate-600"
          )}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Status Badge Components
// =============================================================================

// Note: These badge components are available for future use in credential displays
function _CredentialStatusBadge({ status }: { status: CredentialStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        status === "VERIFIED" && "bg-emerald-100 text-emerald-700",
        status === "PENDING" && "bg-amber-100 text-amber-700",
        status === "REJECTED" && "bg-red-100 text-red-700",
        status === "EXPIRED" && "bg-slate-100 text-slate-700"
      )}
    >
      {status === "VERIFIED" && <CheckCircle className="h-3 w-3" />}
      {status === "PENDING" && <Clock className="h-3 w-3" />}
      {status === "REJECTED" && <XCircle className="h-3 w-3" />}
      {status === "EXPIRED" && <AlertTriangle className="h-3 w-3" />}
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function _ExpiryStatusBadge({ status }: { status: ExpiryStatus }) {
  if (status === "VALID") return null;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        status === "EXPIRING_SOON" && "bg-amber-100 text-amber-700",
        status === "EXPIRED" && "bg-red-100 text-red-700"
      )}
    >
      {status === "EXPIRING_SOON" && <AlertTriangle className="h-3 w-3" />}
      {status === "EXPIRED" && <XCircle className="h-3 w-3" />}
      {status === "EXPIRING_SOON" ? "Expiring Soon" : "Expired"}
    </span>
  );
}

function ComplianceStatusBadge({ status }: { status: ComplianceStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        status === "COMPLIANT" && "bg-emerald-100 text-emerald-700",
        status === "AT_RISK" && "bg-amber-100 text-amber-700",
        status === "NON_COMPLIANT" && "bg-red-100 text-red-700"
      )}
    >
      {status === "COMPLIANT" && <CheckCircle className="h-3 w-3" />}
      {status === "AT_RISK" && <AlertTriangle className="h-3 w-3" />}
      {status === "NON_COMPLIANT" && <XCircle className="h-3 w-3" />}
      {status === "COMPLIANT"
        ? "Compliant"
        : status === "AT_RISK"
        ? "At Risk"
        : "Non-Compliant"}
    </span>
  );
}

// =============================================================================
// Credentials List Component
// =============================================================================

interface CredentialsListProps {
  credentials: NurseCredential[];
  isLoading: boolean;
  onUploadClick: () => void;
  onViewDetail: (credential: NurseCredential) => void;
  onRefetch: () => void;
}

function CredentialsList({
  credentials,
  isLoading,
  onUploadClick,
  onViewDetail,
  onRefetch,
}: CredentialsListProps) {
  if (isLoading) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">
        Loading credentials...
      </div>
    );
  }

  if (credentials.length === 0) {
    return (
      <div className="py-8 text-center">
        <FileCheck className="mx-auto h-10 w-10 text-slate-300" />
        <p className="mt-2 text-sm text-slate-500">No credentials uploaded yet</p>
        <button
          type="button"
          onClick={onUploadClick}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800"
        >
          <Upload className="h-4 w-4" />
          Upload Credential
        </button>
      </div>
    );
  }

  // Group by category
  const licenses = credentials.filter((c) => c.credential_type.is_license);
  const certifications = credentials.filter(
    (c) => !c.credential_type.is_license && c.credential_type.required_for_work
  );
  const other = credentials.filter(
    (c) => !c.credential_type.is_license && !c.credential_type.required_for_work
  );

  const renderCredentialGroup = (title: string, items: NurseCredential[]) => {
    if (items.length === 0) return null;

    return (
      <div className="mt-4 first:mt-0">
        <h4 className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
          {title}
        </h4>
        <div className="space-y-2">
          {items.map((cred) => (
            <CredentialCard
              key={cred.id}
              credential={cred}
              onView={onViewDetail}
              onDelete={onRefetch}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      {renderCredentialGroup("Licenses", licenses)}
      {renderCredentialGroup("Certifications", certifications)}
      {renderCredentialGroup("Other Documents", other)}
    </div>
  );
}

// =============================================================================
// Applications List Component
// =============================================================================

interface ApplicationsListProps {
  applications: ApplicationWithCompliance[];
  isLoading: boolean;
}

function ApplicationsList({ applications, isLoading }: ApplicationsListProps) {
  if (isLoading) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">
        Loading applications...
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">
        No active applications
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {applications.map((app) => (
        <div key={app.id} className="py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">{app.job_title}</span>
                {app.complianceStatus && (
                  <ComplianceStatusBadge status={app.complianceStatus} />
                )}
                {app.complianceLoading && (
                  <span className="text-xs text-slate-400">Checking...</span>
                )}
              </div>
              <div className="mt-1 text-sm text-slate-600">
                {app.facility_name ?? "Unknown Facility"}
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                Status: {app.status.charAt(0) + app.status.slice(1).toLowerCase().replace("_", " ")}
              </div>

              {/* Compliance Issues */}
              {app.compliance && !app.compliance.is_compliant && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {app.compliance.missing.map((issue, idx) => (
                    <span
                      key={`missing-${idx}`}
                      className="inline-flex items-center rounded bg-red-50 px-1.5 py-0.5 text-[0.65rem] text-red-700"
                    >
                      Missing: {issue.credential_type_name}
                    </span>
                  ))}
                  {app.compliance.expired.map((issue, idx) => (
                    <span
                      key={`expired-${idx}`}
                      className="inline-flex items-center rounded bg-red-50 px-1.5 py-0.5 text-[0.65rem] text-red-700"
                    >
                      Expired: {issue.credential_type_name}
                    </span>
                  ))}
                  {app.compliance.not_verified.map((issue, idx) => (
                    <span
                      key={`unverified-${idx}`}
                      className="inline-flex items-center rounded bg-amber-50 px-1.5 py-0.5 text-[0.65rem] text-amber-700"
                    >
                      Pending: {issue.credential_type_name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export default function NurseCompliancePage() {
  const {
    credentials,
    isLoading: credentialsLoading,
    refetch: refetchCredentials,
  } = useNurseCredentials();

  const {
    applications,
    isLoading: applicationsLoading,
    refetch: refetchApplications,
  } = useNurseApplications({
    includeCompliance: true,
    pageSize: 10,
  });

  // Modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState<NurseCredential | null>(null);

  // Filter to active applications only
  const activeApplications = applications.filter((app) =>
    ["APPLIED", "UNDER_REVIEW", "INTERVIEW", "OFFERED"].includes(app.status)
  );

  const stats = computeCredentialStats(credentials);

  const handleRefresh = () => {
    refetchCredentials();
    refetchApplications();
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    refetchCredentials();
    refetchApplications();
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Compliance</h1>
          <p className="mt-1 text-sm text-slate-600">
            Track your credentials and compliance status for active applications.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <NotificationsBell />
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </header>

      {/* Summary Cards */}
      <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <SummaryCard
          label="Total Credentials"
          value={stats.total}
          icon={<FileCheck className="h-5 w-5" />}
        />
        <SummaryCard
          label="Verified"
          value={stats.verified}
          icon={<CheckCircle className="h-5 w-5" />}
          severity="success"
        />
        <SummaryCard
          label="Pending Review"
          value={stats.pending}
          icon={<Clock className="h-5 w-5" />}
          severity="info"
        />
        <SummaryCard
          label="Expiring Soon"
          value={stats.expiringSoon}
          icon={<AlertTriangle className="h-5 w-5" />}
          severity="warning"
        />
        <SummaryCard
          label="Needs Attention"
          value={stats.rejected + stats.expired}
          icon={<XCircle className="h-5 w-5" />}
          severity={stats.rejected + stats.expired > 0 ? "danger" : undefined}
        />
      </section>

      {/* Main Content */}
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {/* Credentials Section */}
        <section className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                My Credentials
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Licenses, certifications, and documents on file.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800"
            >
              <Upload className="h-4 w-4" />
              Upload
            </button>
          </div>
          <div className="mt-4">
            <CredentialsList
              credentials={credentials}
              isLoading={credentialsLoading}
              onUploadClick={() => setShowUploadModal(true)}
              onViewDetail={setSelectedCredential}
              onRefetch={refetchCredentials}
            />
          </div>
        </section>

        {/* Applications Compliance Section */}
        <section className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-slate-400" />
            <h2 className="text-lg font-semibold text-slate-900">
              Application Compliance
            </h2>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            Compliance status for your active job applications.
          </p>
          <div className="mt-4">
            <ApplicationsList
              applications={activeApplications}
              isLoading={applicationsLoading}
            />
          </div>
        </section>
      </div>

      {/* Help Section */}
      <section className="mt-8 rounded-lg border border-blue-200 bg-blue-50 p-6">
        <h3 className="font-semibold text-blue-900">
          Understanding Compliance Status
        </h3>
        <div className="mt-3 grid gap-4 text-sm text-blue-800 sm:grid-cols-3">
          <div className="flex items-start gap-2">
            <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
            <div>
              <span className="font-medium">Compliant</span>
              <p className="mt-0.5 text-blue-700">
                All required credentials are verified and current.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <div>
              <span className="font-medium">At Risk</span>
              <p className="mt-0.5 text-blue-700">
                Some credentials are pending verification or expiring soon.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
            <div>
              <span className="font-medium">Non-Compliant</span>
              <p className="mt-0.5 text-blue-700">
                Missing or expired credentials are blocking your application.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Upload Modal */}
      {showUploadModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-in fade-in duration-200"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowUploadModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">Upload Credential</h2>
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <CredentialUpload
                onSuccess={handleUploadSuccess}
                onCancel={() => setShowUploadModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedCredential && (
        <CredentialDetailModal
          credential={selectedCredential}
          onClose={() => setSelectedCredential(null)}
        />
      )}
    </div>
  );
}
